import React, { useState, useEffect } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import apiClient from '../services/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

/**
 * TopupOBU.jsx — ໜ້າ Topup OBU/UHF
 * ສະແດງ KPI, ຕາຕະລາງທຸລະກຳ Topup, ແນວໂນ້ມ, ອຸປະກອນ
 */
const TopupOBU = ({ lang = 'lo', theme = 'dark', dateRange, customStart: propStart, customEnd: propEnd }) => {
  const [summary, setSummary]       = useState(null);
  const [trend, setTrend]           = useState([]);
  const [transactions, setTxns]     = useState([]);
  const [devices, setDevices]       = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingTxns, setLoadingTxns]       = useState(true);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [days, setDays]             = useState(() => {
    if (dateRange === 'customRange') return 'customRange';
    const filterDaysMap = { filterToday: 1, filter7: 7, filter30: 30, filter90: 90, allSources: 'all' };
    return filterDaysMap[dateRange] !== undefined ? filterDaysMap[dateRange] : 1;
  });
  const [customStart, setCustomStart] = useState(propStart || '');
  const [customEnd, setCustomEnd]     = useState(propEnd || '');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [tab, setTab]               = useState('transactions'); // transactions | devices
  const [page, setPage]             = useState(1);
  const [pagination, setPagination] = useState(null);

  const i18n = {
    lo: {
      title: 'Topup OBU/UHF',
      subtitle: 'ການຕື່ມເງິນ OBU ແລະ UHF ສຳລັບລະບົບຜ່ານດ່ານ',
      totalTopup: 'ລາຍຮັບ Topup ລວມ',
      totalTxn: 'ທຸລະກຳທັງໝົດ',
      avgTopup: 'ຈຳນວນ Topup ສະເລ່ຍ',
      obuRevenue: 'ລາຍຮັບ OBU',
      uhfRevenue: 'ລາຍຮັບ UHF',
      trend: 'ແນວໂນ້ມ Topup',
      txnTable: 'ທຸລະກຳ Topup',
      deviceTable: 'ອຸປະກອນ OBU/UHF',
      txnId: 'ລະຫັດທຸລະກຳ',
      device: 'ອຸປະກອນ',
      type: 'ປະເພດ',
      plate: 'ທະບຽນ',
      amount: 'ຈຳນວນ',
      balBefore: 'ຍອດກ່ອນ',
      balAfter: 'ຍອດຫຼັງ',
      payment: 'ວິທີຊຳລະ',
      station: 'ດ່ານ',
      operator: 'ພະນັກງານ',
      status: 'ສະຖານະ',
      time: 'ເວລາ',
      owner: 'ເຈົ້າຂອງ',
      balance: 'ຍອດຄົງເຫຼືອ',
      topupCount: 'ຈຳນວນ Topup',
      totalTopped: 'ລວມ Topup',
      allTypes: 'ທຸກປະເພດ',
      today: 'ວັນນີ້',
      day7: '7 ວັນ',
      day30: '30 ວັນ',
      day90: '90 ວັນ',
      prev: 'ກ່ອນ',
      next: 'ຕໍ່ໄປ',
      customRange: 'ກຳນົດເອງ',
      fromDate: 'ຈາກວັນທີ',
      toDate: 'ຫາວັນທີ',
      apply: 'ນຳໃຊ້',
    },
    th: {
      title: 'Topup OBU/UHF',
      subtitle: 'การเติมเงิน OBU และ UHF สำหรับระบบผ่านด่าน',
      totalTopup: 'รายได้ Topup รวม',
      totalTxn: 'ธุรกรรมทั้งหมด',
      avgTopup: 'จำนวน Topup เฉลี่ย',
      obuRevenue: 'รายได้ OBU',
      uhfRevenue: 'รายได้ UHF',
      trend: 'แนวโน้ม Topup',
      txnTable: 'ธุรกรรม Topup',
      deviceTable: 'อุปกรณ์ OBU/UHF',
      txnId: 'รหัสธุรกรรม',
      device: 'อุปกรณ์',
      type: 'ประเภท',
      plate: 'ทะเบียน',
      amount: 'จำนวน',
      balBefore: 'ยอดก่อน',
      balAfter: 'ยอดหลัง',
      payment: 'วิธีชำระ',
      station: 'ด่าน',
      operator: 'พนักงาน',
      status: 'สถานะ',
      time: 'เวลา',
      owner: 'เจ้าของ',
      balance: 'ยอดคงเหลือ',
      topupCount: 'จำนวน Topup',
      totalTopped: 'รวม Topup',
      allTypes: 'ทุกประเภท',
      today: 'วันนี้',
      day7: '7 วัน',
      day30: '30 วัน',
      day90: '90 วัน',
      prev: 'ก่อน',
      next: 'ถัดไป',
      customRange: 'กำหนดเอง',
      fromDate: 'จากวันที่',
      toDate: 'ถึงวันที่',
      apply: 'นำไปใช้',
    },
    en: {
      title: 'Topup OBU/UHF',
      subtitle: 'OBU and UHF top-up transactions for toll pass system',
      totalTopup: 'Total Topup Revenue',
      totalTxn: 'Total Transactions',
      avgTopup: 'Avg Topup Amount',
      obuRevenue: 'OBU Revenue',
      uhfRevenue: 'UHF Revenue',
      trend: 'Topup Trend',
      txnTable: 'Topup Transactions',
      deviceTable: 'OBU/UHF Devices',
      txnId: 'Transaction ID',
      device: 'Device',
      type: 'Type',
      plate: 'Plate',
      amount: 'Amount',
      balBefore: 'Bal. Before',
      balAfter: 'Bal. After',
      payment: 'Payment',
      station: 'Station',
      operator: 'Operator',
      status: 'Status',
      time: 'Time',
      owner: 'Owner',
      balance: 'Balance',
      topupCount: 'Topup Count',
      totalTopped: 'Total Topped',
      allTypes: 'All Types',
      today: 'Today',
      day7: '7 Days',
      day30: '30 Days',
      day90: '90 Days',
      prev: 'Prev',
      next: 'Next',
      customRange: 'Custom Range',
      fromDate: 'From Date',
      toDate: 'To Date',
      apply: 'Apply',
    },
  };
  const t = (k) => i18n[lang]?.[k] || i18n.en[k] || k;

  const fmt = (n) => Number(n || 0).toLocaleString();
  const fmtM = (v) => {
    if (v === undefined || v === null) return '₭0';
    const num = Number(v);
    if (isNaN(num)) return '₭0';
    if (num >= 1e9) {
      return `₭${parseFloat((num / 1e9).toFixed(2))}B`;
    } else if (num >= 1e6) {
      return `₭${parseFloat((num / 1e6).toFixed(2))}M`;
    } else {
      return `₭${num.toLocaleString()}`;
    }
  };

  const loadSummaryAndTrend = async (d, type) => {
    setLoadingSummary(true);
    try {
      let params = {};
      if (d === 'customRange') {
        params.startDate = customStart;
        params.endDate = customEnd;
      } else {
        params.days = d;
      }
      if (type !== 'ALL') params.type = type;

      const [sumRes, trendRes] = await Promise.all([
        apiClient.topupGetSummary(params),
        apiClient.topupGetTrend(params),
      ]);

      if (sumRes?.success)   setSummary(sumRes.data);
      if (trendRes?.success) setTrend(Array.isArray(trendRes.data) ? trendRes.data : []);
    } catch (e) {
      console.warn('Topup OBU summary/trend load error:', e.message);
    }
    setLoadingSummary(false);
  };

  const loadTransactions = async (d, type, p) => {
    setLoadingTxns(true);
    try {
      let listParams = { page: p, limit: 50 };
      if (d === 'customRange') {
        listParams.startDate = customStart;
        listParams.endDate = customEnd;
      } else {
        listParams.days = d;
      }
      if (type !== 'ALL') listParams.type = type;

      const listRes = await apiClient.topupGetList(listParams);

      if (listRes?.success) {
        setTxns(Array.isArray(listRes.data) ? listRes.data : []);
        setPagination(listRes.pagination || null);
      }
    } catch (e) {
      console.warn('Topup OBU list load error:', e.message);
    }
    setLoadingTxns(false);
  };

  const loadDevices = async (type) => {
    setLoadingDevices(true);
    try {
      const devRes = await apiClient.topupGetDevices(type !== 'ALL' ? { type } : {});
      if (devRes?.success) {
        setDevices(Array.isArray(devRes.data) ? devRes.data : []);
      }
    } catch (e) {
      console.warn('Topup OBU devices load error:', e.message);
    }
    setLoadingDevices(false);
  };

  // Sync state if props change
  useEffect(() => {
    if (dateRange) {
      if (dateRange === 'customRange') {
        setDays('customRange');
        if (propStart) setCustomStart(propStart);
        if (propEnd) setCustomEnd(propEnd);
      } else {
        const filterDaysMap = { filterToday: 1, filter7: 7, filter30: 30, filter90: 90, allSources: 'all' };
        const d = filterDaysMap[dateRange];
        if (d !== undefined) setDays(d);
      }
    }
  }, [dateRange, propStart, propEnd]);

  // 1. Hook for days or typeFilter changes (resets page and summary)
  useEffect(() => {
    if (days === 'customRange' && (!customStart || !customEnd)) return;
    setPage(1);
    loadSummaryAndTrend(days, typeFilter);
    loadTransactions(days, typeFilter, 1);
    if (tab === 'devices') {
      loadDevices(typeFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, typeFilter]);

  // 2. Hook for page changes
  useEffect(() => {
    if (days === 'customRange' && (!customStart || !customEnd)) return;
    loadTransactions(days, typeFilter, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // 3. Hook for tab changes (lazy loading devices)
  useEffect(() => {
    if (tab === 'devices') {
      loadDevices(typeFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleCustomRangeApply = () => {
    if (!customStart || !customEnd) return;
    if (days === 'customRange') {
      setPage(1);
      loadSummaryAndTrend('customRange', typeFilter);
      loadTransactions('customRange', typeFilter, 1);
      if (tab === 'devices') {
        loadDevices(typeFilter);
      }
    } else {
      setDays('customRange');
      setPage(1);
    }
  };

  const gridColor = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const tickColor = theme === 'dark' ? '#8892a4' : '#546e7a';
  const tooltip   = { backgroundColor: 'rgba(255,255,255,0.96)', titleColor: '#2c3e50', bodyColor: '#546e7a', borderColor: '#e1e4e8', borderWidth: 1, padding: 12 };

  const trendChartData = {
    labels: trend.map(d => {
      const parts = String(d.date).split('T')[0].split('-');
      return parts.length === 3 ? `${parts[1]}/${parts[2]}` : d.date;
    }),
    datasets: [
      {
        label: 'OBU',
        data: trend.map(d => d.obuAmount),
        borderColor: '#1890ff',
        backgroundColor: 'rgba(24,144,255,0.08)',
        fill: true, tension: 0.4, pointRadius: 2, borderWidth: 2,
      },
      {
        label: 'UHF',
        data: trend.map(d => d.uhfAmount),
        borderColor: '#9254de',
        backgroundColor: 'rgba(146,84,222,0.08)',
        fill: true, tension: 0.4, pointRadius: 2, borderWidth: 2,
      },
    ],
  };

  const trendOptions = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: true, position: 'top', labels: { color: tickColor, font: { size: 11 }, usePointStyle: true, padding: 16 } },
      tooltip,
    },
    scales: {
      x: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 10 }, maxTicksLimit: 15 } },
      y: { grid: { color: gridColor }, ticks: { color: tickColor, font: { size: 11 }, callback: v => `₭${v.toFixed(1)}B` } },
    },
  };

  const kpis = summary ? [
    { label: t('totalTopup'),  val: fmtM(summary.totalAmount),  sub: `${fmt(summary.totalTxn)} txn`,          color: '#1890ff' },
    { label: t('avgTopup'),    val: `₭ ${fmt(summary.avgAmount)}`, sub: `${fmt(summary.obuCount)} OBU / ${fmt(summary.uhfCount)} UHF`, color: '#9254de' },
    { label: t('obuRevenue'),  val: fmtM(summary.obuAmount),    sub: `${fmt(summary.obuCount)} transactions`, color: '#fa8c16' },
    { label: t('uhfRevenue'),  val: fmtM(summary.uhfAmount),    sub: `${fmt(summary.uhfCount)} transactions`, color: '#52c41a' },
  ] : [];

  const statusStyle = {
    ok:      { bg: 'rgba(82,196,26,.15)',  color: '#52c41a', text: 'OK' },
    pending: { bg: 'rgba(250,173,20,.15)', color: '#faad14', text: 'PEND' },
    failed:  { bg: 'rgba(245,34,45,.15)',  color: '#f5222d', text: 'FAIL' },
  };

  const typeStyle = {
    OBU: { bg: 'rgba(24,144,255,.15)',  color: '#1890ff' },
    UHF: { bg: 'rgba(146,84,222,.15)', color: '#9254de' },
    MIFARE: { bg: 'rgba(82,196,26,.15)', color: '#52c41a' },
  };

  return (
    <div className="main">
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      {/* Header */}
      <div className="section-header" style={{ flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div className="section-title">{t('title')}</div>
          <div className="section-sub">{t('subtitle')}</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[{v:1,l:t('today')},{v:7,l:t('day7')},{v:30,l:t('day30')},{v:90,l:t('day90')}].map(d => (
              <div key={d.v} className={`filter-chip ${days === d.v ? 'active' : ''}`}
                onClick={() => { setDays(d.v); setPage(1); }} style={{ cursor: 'pointer' }}>
                {d.l}
              </div>
            ))}
          </div>

          <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 4px' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('fromDate')}:</span>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'var(--text)',
                  fontFamily: 'var(--font)',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{t('toDate')}:</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={{
                  background: 'var(--bg3)',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  padding: '4px 8px',
                  fontSize: '12px',
                  color: 'var(--text)',
                  fontFamily: 'var(--font)',
                  outline: 'none',
                }}
              />
            </div>
            <button
              onClick={handleCustomRangeApply}
              disabled={!customStart || !customEnd}
              style={{
                background: 'var(--blue)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '5px 12px',
                fontSize: '12px',
                cursor: (!customStart || !customEnd) ? 'not-allowed' : 'pointer',
                opacity: (!customStart || !customEnd) ? 0.5 : 1,
                fontFamily: 'var(--font)',
                fontWeight: '600',
                transition: 'all 0.2s ease',
              }}
            >
              {t('apply')}
            </button>
          </div>

          <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 4px' }} />

          <div style={{ display: 'flex', gap: '4px' }}>
            {['ALL','OBU','UHF'].map(tp => (
              <div key={tp} className={`filter-chip ${typeFilter === tp ? 'active' : ''}`}
                onClick={() => { setTypeFilter(tp); setPage(1); }} style={{ cursor: 'pointer' }}>
                {tp === 'ALL' ? t('allTypes') : tp}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {loadingSummary && !summary ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text3)' }}>Loading...</div>
      ) : (
        <>
          <div className="kpi-grid" style={{ marginBottom: '16px' }}>
            {kpis.map(k => (
              <div key={k.label} className="kpi">
                <div className="kpi-accent" style={{ background: k.color }} />
                <div className="kpi-label">{k.label}</div>
                <div className="kpi-value" style={{ color: k.color }}>{k.val}</div>
                <div className="kpi-sub kpi-up">{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Trend Chart */}
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-title">{t('trend')}</div>
            <div className="card-sub">OBU vs UHF — ₭ Billion</div>
            <div style={{ height: '200px', marginTop: '12px' }}>
              <Line data={trendChartData} options={trendOptions} />
            </div>
          </div>

          {/* Tab switcher */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
            {[{k:'transactions',l:t('txnTable')},{k:'devices',l:t('deviceTable')}].map(tb => (
              <button key={tb.k} onClick={() => setTab(tb.k)}
                style={{
                  padding: '7px 18px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                  fontFamily: 'var(--font)', fontSize: '12px', fontWeight: tab === tb.k ? '600' : '400',
                  background: tab === tb.k ? '#1890ff' : 'var(--bg3)',
                  color: tab === tb.k ? '#fff' : 'var(--text2)',
                }}>
                {tb.l}
              </button>
            ))}
          </div>

          {/* Transactions Table */}
          {tab === 'transactions' && (
            <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
              {loadingTxns && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', zIndex: 10, color: 'var(--text)',
                  fontSize: '14px', fontWeight: '500', backdropFilter: 'blur(1px)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="spinner" style={{
                      display: 'inline-block', width: '16px', height: '16px',
                      border: '2px solid var(--text3)', borderTopColor: '#1890ff',
                      borderRadius: '50%', animation: 'spin 1s linear infinite'
                    }} />
                    Loading Transactions...
                  </div>
                </div>
              )}
              <table className="data-table" style={{ opacity: loadingTxns ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                <thead>
                  <tr>
                    <th>{t('txnId')}</th>
                    <th>{t('type')}</th>
                    <th>{t('plate')}</th>
                    <th style={{ textAlign: 'right' }}>{t('amount')}</th>
                    <th style={{ textAlign: 'right' }}>{t('balBefore')}</th>
                    <th style={{ textAlign: 'right' }}>{t('balAfter')}</th>
                    <th>{t('payment')}</th>
                    <th>{t('station')}</th>
                    <th>{t('operator')}</th>
                    <th>{t('status')}</th>
                    <th>{t('time')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(tx => {
                    const st = statusStyle[tx.status] || statusStyle.ok;
                    const dt = typeStyle[String(tx.deviceType || '').toUpperCase()] || typeStyle.OBU;
                    return (
                      <tr key={tx.id}>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--text3)' }}>{tx.id}</td>
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', background: dt.bg, color: dt.color }}>
                            {tx.deviceType}
                          </span>
                        </td>
                        <td style={{ fontSize: '11px', color: 'var(--text2)' }}>{tx.plate || '-'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '12px', fontWeight: '600', color: '#1890ff' }}>
                          ₭ {fmt(tx.amount)}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--text3)' }}>
                          ₭ {fmt(tx.balanceBefore)}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '11px', color: '#52c41a' }}>
                          ₭ {fmt(tx.balanceAfter)}
                        </td>
                        <td style={{ fontSize: '11px' }}>
                          <span style={{ padding: '2px 6px', borderRadius: '4px', fontSize: '10px', background: 'var(--bg4)', color: 'var(--text2)' }}>
                            {String(tx.paymentMethod || '').toLowerCase()}
                          </span>
                        </td>
                        <td style={{ fontSize: '11px', color: 'var(--text3)' }}>{tx.stationName || tx.stationCode || '-'}</td>
                        <td style={{ fontSize: '11px', color: 'var(--text2)' }}>{tx.operator || '-'}</td>
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', background: st.bg, color: st.color }}>
                            {st.text}
                          </span>
                        </td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: '10px', color: 'var(--text3)' }}>
                          {tx.time ? new Date(tx.time).toLocaleString() : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', padding: '12px 16px', borderTop: '1px solid var(--border)', fontSize: '12px', color: 'var(--text3)' }}>
                  <span>{pagination.page} / {pagination.totalPages} ({fmt(pagination.total)} rows)</span>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                    style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: page <= 1 ? 'not-allowed' : 'pointer', fontFamily: 'var(--font)', fontSize: '11px' }}>
                    {t('prev')}
                  </button>
                  <button onClick={() => setPage(p => p + 1)} disabled={!pagination.hasNextPage}
                    style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg3)', color: 'var(--text2)', cursor: !pagination.hasNextPage ? 'not-allowed' : 'pointer', fontFamily: 'var(--font)', fontSize: '11px' }}>
                    {t('next')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Devices Table */}
          {tab === 'devices' && (
            <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
              {loadingDevices && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', zIndex: 10, color: 'var(--text)',
                  fontSize: '14px', fontWeight: '500', backdropFilter: 'blur(1px)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="spinner" style={{
                      display: 'inline-block', width: '16px', height: '16px',
                      border: '2px solid var(--text3)', borderTopColor: '#1890ff',
                      borderRadius: '50%', animation: 'spin 1s linear infinite'
                    }} />
                    Loading Devices...
                  </div>
                </div>
              )}
              <table className="data-table" style={{ opacity: loadingDevices ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                <thead>
                  <tr>
                    <th>{t('device')}</th>
                    <th>{t('type')}</th>
                    <th>{t('plate')}</th>
                    <th>{t('owner')}</th>
                    <th style={{ textAlign: 'right' }}>{t('balance')}</th>
                    <th style={{ textAlign: 'right' }}>{t('topupCount')}</th>
                    <th style={{ textAlign: 'right' }}>{t('totalTopped')}</th>
                    <th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map(d => {
                    const dt = typeStyle[String(d.deviceType || '').toUpperCase()] || typeStyle.OBU;
                    return (
                      <tr key={d.deviceId}>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: '11px', fontWeight: '600' }}>{d.deviceId}</td>
                        <td>
                          <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700', background: dt.bg, color: dt.color }}>
                            {d.deviceType}
                          </span>
                        </td>
                        <td style={{ fontSize: '11px', color: 'var(--text2)' }}>{d.plate || '-'}</td>
                        <td style={{ fontSize: '12px' }}>{d.owner || '-'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '12px', fontWeight: '600', color: '#52c41a' }}>
                          ₭ {fmt(d.balance)}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '12px' }}>{fmt(d.topupCount)}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: '12px', color: '#1890ff' }}>
                          ₭ {fmt(d.totalTopped)}
                        </td>
                        <td>
                          <span style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                            background: d.isActive ? 'rgba(82,196,26,.15)' : 'rgba(250,140,22,.15)',
                            color: d.isActive ? '#52c41a' : '#fa8c16',
                          }}>
                            {d.isActive ? '✓ Active' : '⏸ Inactive'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopupOBU;
